/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * SuperAdmin Command Center — Bento Grid Dashboard
 * ============================================================================
 *
 * Premium dashboard with animated counters, SVG charts, 3D flip leaderboards,
 * detail flyout panel, and cascading entrance animations.
 *
 * All data is mock for now — backend endpoints to be wired in later.
 *
 * ============================================================================
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2, DollarSign, Users, Briefcase,
  TrendingUp, TrendingDown, RefreshCw, Plus,
  Crown, AlertTriangle, Clock, CheckCircle,
  XCircle, UserPlus, CreditCard, CalendarClock,
  ChevronRight, X, ExternalLink, Activity,
  Zap, Shield, Eye, ArrowUpRight,
  BarChart3, PieChart, Search,
} from 'lucide-react';

// ============================================================================
// MOCK DATA
// ============================================================================

interface MockOrg {
  id: string;
  name: string;
  plan: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  status: 'ACTIVE' | 'TRIAL' | 'PAST_DUE' | 'SUSPENDED';
  monthlyRevenue: number;
  yearlyRevenue: number;
  users: number;
  joinedAt: string;
  country: string;
  modules: string[];
  overdueAmount?: number;
  overdueDays?: number;
}

interface MockActivity {
  id: string;
  type: 'ORG_CREATED' | 'PAYMENT_RECEIVED' | 'ORG_SUSPENDED' | 'USER_REGISTERED' | 'WITHDRAWAL_REQUESTED';
  entityName: string;
  description: string;
  createdAt: string;
}

interface MockAttention {
  type: 'PENDING_REGISTRATION' | 'FAILED_PAYMENT' | 'PENDING_WITHDRAWAL' | 'EXPIRING_TRIAL';
  title: string;
  description: string;
  count: number;
  urgency: 'HIGH' | 'MEDIUM' | 'LOW';
  route: string;
}

const MOCK_ORGS: MockOrg[] = [
  { id: '1', name: 'Conakry Motors SARL', plan: 'ENTERPRISE', status: 'ACTIVE', monthlyRevenue: 2500, yearlyRevenue: 28500, users: 45, joinedAt: '2025-03-15', country: 'GN', modules: ['VENTA', 'HR', 'COMPTA'] },
  { id: '2', name: 'Bamako Trading Co', plan: 'ENTERPRISE', status: 'ACTIVE', monthlyRevenue: 2200, yearlyRevenue: 25800, users: 38, joinedAt: '2025-04-22', country: 'ML', modules: ['VENTA', 'COMPTA'] },
  { id: '3', name: 'Dakar Fresh Market', plan: 'PROFESSIONAL', status: 'ACTIVE', monthlyRevenue: 1800, yearlyRevenue: 21200, users: 28, joinedAt: '2025-02-10', country: 'SN', modules: ['VENTA', 'HR'] },
  { id: '4', name: 'Abidjan Logistics Plus', plan: 'PROFESSIONAL', status: 'ACTIVE', monthlyRevenue: 1650, yearlyRevenue: 19500, users: 32, joinedAt: '2025-05-08', country: 'CI', modules: ['VENTA'] },
  { id: '5', name: 'Ouaga Tech Solutions', plan: 'PROFESSIONAL', status: 'ACTIVE', monthlyRevenue: 1400, yearlyRevenue: 16200, users: 22, joinedAt: '2025-06-01', country: 'BF', modules: ['VENTA', 'PM'] },
  { id: '6', name: 'Niamey Electronics', plan: 'STARTER', status: 'PAST_DUE', monthlyRevenue: 800, yearlyRevenue: 9200, users: 12, joinedAt: '2025-07-14', country: 'NE', modules: ['VENTA'], overdueAmount: 2400, overdueDays: 45 },
  { id: '7', name: 'Lome Import-Export', plan: 'PROFESSIONAL', status: 'PAST_DUE', monthlyRevenue: 1200, yearlyRevenue: 13800, users: 18, joinedAt: '2025-04-30', country: 'TG', modules: ['VENTA', 'COMPTA'], overdueAmount: 1800, overdueDays: 32 },
  { id: '8', name: 'Cotonou Pharma Plus', plan: 'STARTER', status: 'PAST_DUE', monthlyRevenue: 600, yearlyRevenue: 6900, users: 8, joinedAt: '2025-08-20', country: 'BJ', modules: ['VENTA'], overdueAmount: 1200, overdueDays: 28 },
  { id: '9', name: 'Freetown Agrotech', plan: 'STARTER', status: 'PAST_DUE', monthlyRevenue: 500, yearlyRevenue: 5500, users: 6, joinedAt: '2025-09-05', country: 'SL', modules: ['VENTA'], overdueAmount: 750, overdueDays: 15 },
  { id: '10', name: 'Monrovia Steel Works', plan: 'STARTER', status: 'PAST_DUE', monthlyRevenue: 450, yearlyRevenue: 4800, users: 5, joinedAt: '2025-10-11', country: 'LR', modules: ['VENTA'], overdueAmount: 450, overdueDays: 8 },
  { id: '11', name: 'Banjul Textiles Ltd', plan: 'STARTER', status: 'TRIAL', monthlyRevenue: 0, yearlyRevenue: 0, users: 4, joinedAt: '2026-02-15', country: 'GM', modules: ['VENTA'] },
  { id: '12', name: 'Nouakchott Fish Co', plan: 'STARTER', status: 'TRIAL', monthlyRevenue: 0, yearlyRevenue: 0, users: 3, joinedAt: '2026-02-28', country: 'MR', modules: ['VENTA'] },
  { id: '13', name: 'Praia Digital Agency', plan: 'PROFESSIONAL', status: 'ACTIVE', monthlyRevenue: 1100, yearlyRevenue: 12800, users: 15, joinedAt: '2025-11-20', country: 'CV', modules: ['VENTA', 'PM'] },
  { id: '14', name: 'Bissau Coffee Export', plan: 'STARTER', status: 'ACTIVE', monthlyRevenue: 350, yearlyRevenue: 3900, users: 4, joinedAt: '2025-12-05', country: 'GW', modules: ['VENTA'] },
  { id: '15', name: 'Accra Gold Mining Ltd', plan: 'ENTERPRISE', status: 'ACTIVE', monthlyRevenue: 3200, yearlyRevenue: 37500, users: 62, joinedAt: '2025-01-20', country: 'GH', modules: ['VENTA', 'HR', 'COMPTA', 'PM'] },
];

const MOCK_REVENUE_TREND = [
  { month: 'Apr', revenue: 4200 },
  { month: 'May', revenue: 4800 },
  { month: 'Jun', revenue: 5100 },
  { month: 'Jul', revenue: 5600 },
  { month: 'Aug', revenue: 5200 },
  { month: 'Sep', revenue: 5900 },
  { month: 'Oct', revenue: 6400 },
  { month: 'Nov', revenue: 6800 },
  { month: 'Dec', revenue: 7200 },
  { month: 'Jan', revenue: 7800 },
  { month: 'Feb', revenue: 8100 },
  { month: 'Mar', revenue: 8450 },
];

const MOCK_ACTIVITIES: MockActivity[] = [
  { id: '1', type: 'ORG_CREATED', entityName: 'Banjul Textiles Ltd', description: 'Starter Plan — Trial', createdAt: new Date(Date.now() - 3 * 60_000).toISOString() },
  { id: '2', type: 'PAYMENT_RECEIVED', entityName: 'Accra Gold Mining Ltd', description: '$3,200.00 — Enterprise', createdAt: new Date(Date.now() - 18 * 60_000).toISOString() },
  { id: '3', type: 'USER_REGISTERED', entityName: 'Praia Digital Agency', description: '2 new users added', createdAt: new Date(Date.now() - 45 * 60_000).toISOString() },
  { id: '4', type: 'PAYMENT_RECEIVED', entityName: 'Conakry Motors SARL', description: '$2,500.00 — Enterprise', createdAt: new Date(Date.now() - 2 * 3600_000).toISOString() },
  { id: '5', type: 'ORG_SUSPENDED', entityName: 'Niamey Electronics', description: 'Payment overdue 45 days', createdAt: new Date(Date.now() - 4 * 3600_000).toISOString() },
  { id: '6', type: 'WITHDRAWAL_REQUESTED', entityName: 'Commission Payout', description: '$850.00 requested', createdAt: new Date(Date.now() - 6 * 3600_000).toISOString() },
  { id: '7', type: 'ORG_CREATED', entityName: 'Nouakchott Fish Co', description: 'Starter Plan — Trial', createdAt: new Date(Date.now() - 12 * 3600_000).toISOString() },
  { id: '8', type: 'PAYMENT_RECEIVED', entityName: 'Dakar Fresh Market', description: '$1,800.00 — Professional', createdAt: new Date(Date.now() - 24 * 3600_000).toISOString() },
];

const MOCK_ATTENTION: MockAttention[] = [
  { type: 'FAILED_PAYMENT', title: '5 Past Due Payments', description: '$6,600 total outstanding', count: 5, urgency: 'HIGH', route: '/superadmin/organizations?status=SUSPENDED' },
  { type: 'PENDING_REGISTRATION', title: '3 Registration Requests', description: 'Awaiting approval', count: 3, urgency: 'MEDIUM', route: '/superadmin/organizations?status=PENDING_APPROVAL' },
  { type: 'PENDING_WITHDRAWAL', title: '2 Pending Withdrawals', description: '$1,250 total requested', count: 2, urgency: 'LOW', route: '/superadmin/withdrawals?status=PENDING' },
  { type: 'EXPIRING_TRIAL', title: '2 Trials Expiring', description: 'Within next 7 days', count: 2, urgency: 'LOW', route: '/superadmin/organizations?status=EXPIRED_TRIAL' },
];

// Derived mock stats
const MOCK_STATS = {
  totalOrgs: MOCK_ORGS.length,
  activeOrgs: MOCK_ORGS.filter(o => o.status === 'ACTIVE').length,
  trialOrgs: MOCK_ORGS.filter(o => o.status === 'TRIAL').length,
  pastDueOrgs: MOCK_ORGS.filter(o => o.status === 'PAST_DUE').length,
  totalUsers: MOCK_ORGS.reduce((s, o) => s + o.users, 0),
  activeSessions: 28,
  totalMRR: 8450,
  activeMRR: 7650,
  trialMRR: 0,
  totalPartners: 186,
  newMRR: 650,
  churnedMRR: 200,
  expansionMRR: 100,
};

const SUBSCRIPTION_DISTRIBUTION = [
  { label: 'Active', count: MOCK_STATS.activeOrgs, color: '#22c55e' },
  { label: 'Trial', count: MOCK_STATS.trialOrgs, color: '#eab308' },
  { label: 'Past Due', count: MOCK_STATS.pastDueOrgs, color: '#f43f5e' },
  { label: 'Cancelled', count: 1, color: '#78716c' },
];

// ============================================================================
// SVG HELPERS
// ============================================================================

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function donutSlicePath(cx: number, cy: number, outerR: number, innerR: number, startDeg: number, endDeg: number): string {
  if (endDeg - startDeg >= 360) endDeg = startDeg + 359.9999;
  const sO = polarToCartesian(cx, cy, outerR, startDeg);
  const eO = polarToCartesian(cx, cy, outerR, endDeg);
  const sI = polarToCartesian(cx, cy, innerR, startDeg);
  const eI = polarToCartesian(cx, cy, innerR, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${sO.x} ${sO.y} A ${outerR} ${outerR} 0 ${large} 1 ${eO.x} ${eO.y} L ${eI.x} ${eI.y} A ${innerR} ${innerR} 0 ${large} 0 ${sI.x} ${sI.y} Z`;
}

// ============================================================================
// ANIMATED COUNTER
// ============================================================================

function AnimatedCounter({ value, prefix = '', suffix = '', duration = 1200 }: {
  value: number; prefix?: string; suffix?: string; duration?: number;
}) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const to = value;

    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutExpo
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setDisplay(Math.round(from + (to - from) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return <>{prefix}{display.toLocaleString()}{suffix}</>;
}

// ============================================================================
// MINI DONUT (for hero card)
// ============================================================================

function MiniDonut({ data, size = 64 }: {
  data: { value: number; color: string }[];
  size?: number;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 2;
  const innerR = outerR * 0.65;
  const gap = 2;

  let currentAngle = 0;
  const slices = data.map((d) => {
    const sweep = (d.value / total) * 360 - gap;
    const startAngle = currentAngle + gap / 2;
    const endAngle = startAngle + Math.max(sweep, 1);
    currentAngle += (d.value / total) * 360;
    return { ...d, path: donutSlicePath(cx, cy, outerR, innerR, startAngle, endAngle) };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="cc-mini-donut">
      {slices.map((s, i) => (
        <path key={i} d={s.path} fill={s.color} opacity={0.88} />
      ))}
    </svg>
  );
}

// ============================================================================
// AREA CHART (revenue trend)
// ============================================================================

function AreaChart({ data, width = 520, height = 180 }: {
  data: { month: string; revenue: number }[];
  width?: number;
  height?: number;
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const padX = 40;
  const padY = 24;
  const padBottom = 28;
  const chartW = width - padX * 2;
  const chartH = height - padY - padBottom;
  const maxVal = Math.max(...data.map(d => d.revenue));
  const minVal = Math.min(...data.map(d => d.revenue));
  const range = maxVal - minVal || 1;

  const points = data.map((d, i) => ({
    x: padX + (i / (data.length - 1)) * chartW,
    y: padY + chartH - ((d.revenue - minVal) / range) * chartH,
    ...d,
  }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padY + chartH} L ${points[0].x} ${padY + chartH} Z`;

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} className="cc-area-chart" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="cc-area-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-brand-primary)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--color-brand-primary)" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Horizontal grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
        const y = padY + chartH - pct * chartH;
        const val = Math.round(minVal + pct * range);
        return (
          <g key={pct}>
            <line x1={padX} y1={y} x2={width - padX} y2={y} stroke="var(--color-border-subtle)" strokeWidth="0.5" strokeDasharray="4 4" />
            <text x={padX - 6} y={y + 4} textAnchor="end" className="cc-chart-label">${(val / 1000).toFixed(1)}k</text>
          </g>
        );
      })}

      {/* Area fill */}
      <path d={areaPath} fill="url(#cc-area-grad)" className="cc-area-fill" />

      {/* Line */}
      <path d={linePath} fill="none" stroke="var(--color-brand-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="cc-area-line" />

      {/* Data points + labels */}
      {points.map((p, i) => (
        <g key={i}
          onMouseEnter={() => setHoveredIdx(i)}
          onMouseLeave={() => setHoveredIdx(null)}
          style={{ cursor: 'pointer' }}
        >
          {/* Larger invisible hit area */}
          <circle cx={p.x} cy={p.y} r={12} fill="transparent" />

          {/* Visible dot */}
          <circle
            cx={p.x} cy={p.y}
            r={hoveredIdx === i ? 5 : 3}
            fill={hoveredIdx === i ? 'var(--color-brand-primary)' : 'var(--color-bg-secondary)'}
            stroke="var(--color-brand-primary)"
            strokeWidth="2"
            style={{ transition: 'r 0.15s ease' }}
          />

          {/* Month label */}
          <text x={p.x} y={height - 6} textAnchor="middle" className="cc-chart-label">{p.month}</text>

          {/* Hover tooltip */}
          {hoveredIdx === i && (
            <g>
              <rect x={p.x - 38} y={p.y - 32} width={76} height={22} rx={6}
                fill="var(--color-bg-secondary)" stroke="var(--color-border-subtle)" />
              <text x={p.x} y={p.y - 17} textAnchor="middle" className="cc-chart-tooltip-text">
                ${p.revenue.toLocaleString()}
              </text>
            </g>
          )}
        </g>
      ))}
    </svg>
  );
}

// ============================================================================
// ORG PROFILE MODAL
// ============================================================================

function OrgProfileModal({ org, onClose }: { org: MockOrg | null; onClose: () => void }) {
  useEffect(() => {
    if (!org) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [org, onClose]);

  if (!org) return null;

  const planColors: Record<string, string> = { STARTER: '#3b82f6', PROFESSIONAL: '#8b5cf6', ENTERPRISE: '#f59e0b' };
  const statusColors: Record<string, string> = { ACTIVE: '#10b981', TRIAL: '#f59e0b', PAST_DUE: '#ef4444', SUSPENDED: '#ef4444' };

  // Mock enriched data
  const subscribedSince = new Date(org.joinedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const totalPaid = org.yearlyRevenue + Math.round(org.monthlyRevenue * 0.6);
  const amountDue = org.overdueAmount || 0;
  const nextBilling = new Date(Date.now() + 12 * 86400_000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const mockPayments = [
    { date: 'Feb 2026', amount: org.monthlyRevenue, status: 'Paid' },
    { date: 'Jan 2026', amount: org.monthlyRevenue, status: 'Paid' },
    { date: 'Dec 2025', amount: org.monthlyRevenue, status: amountDue > 0 ? 'Failed' : 'Paid' },
  ];

  return (
    <div className="cc-orgmodal-overlay" onClick={onClose}>
      <div className="cc-orgmodal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        {/* Header */}
        <div className="cc-orgmodal-header">
          <div className="cc-orgmodal-header-left">
            <div className="cc-orgmodal-avatar" style={{ background: `${planColors[org.plan]}18`, color: planColors[org.plan] }}>
              <Building2 size={22} />
            </div>
            <div>
              <h2 className="cc-orgmodal-name">{org.name}</h2>
              <div className="cc-orgmodal-badges">
                <span className="cc-orgmodal-plan-badge" style={{ background: `${planColors[org.plan]}20`, color: planColors[org.plan] }}>{org.plan}</span>
                <span className="cc-orgmodal-status-badge" style={{ background: `${statusColors[org.status]}15`, color: statusColors[org.status] }}>
                  <span className="cc-orgmodal-status-dot" style={{ background: statusColors[org.status] }} />
                  {org.status}
                </span>
              </div>
            </div>
          </div>
          <button className="cc-orgmodal-close" onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>

        {/* Key Metrics Grid */}
        <div className="cc-orgmodal-metrics">
          <div className="cc-orgmodal-metric">
            <DollarSign size={16} className="cc-orgmodal-metric-icon" style={{ color: '#10b981' }} />
            <div>
              <span className="cc-orgmodal-metric-label">Total Paid</span>
              <span className="cc-orgmodal-metric-value">${totalPaid.toLocaleString()}</span>
            </div>
          </div>
          <div className="cc-orgmodal-metric">
            <CreditCard size={16} className="cc-orgmodal-metric-icon" style={{ color: '#3b82f6' }} />
            <div>
              <span className="cc-orgmodal-metric-label">Monthly Plan</span>
              <span className="cc-orgmodal-metric-value">${org.monthlyRevenue.toLocaleString()}/mo</span>
            </div>
          </div>
          <div className="cc-orgmodal-metric">
            <AlertTriangle size={16} className="cc-orgmodal-metric-icon" style={{ color: amountDue > 0 ? '#ef4444' : 'var(--color-text-muted)' }} />
            <div>
              <span className="cc-orgmodal-metric-label">Amount Due</span>
              <span className="cc-orgmodal-metric-value" style={{ color: amountDue > 0 ? '#ef4444' : undefined }}>
                {amountDue > 0 ? `$${amountDue.toLocaleString()}` : '$0'}
              </span>
            </div>
          </div>
          <div className="cc-orgmodal-metric">
            <Users size={16} className="cc-orgmodal-metric-icon" style={{ color: '#8b5cf6' }} />
            <div>
              <span className="cc-orgmodal-metric-label">Users</span>
              <span className="cc-orgmodal-metric-value">{org.users}</span>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="cc-orgmodal-section">
          <h4 className="cc-orgmodal-section-title">Subscription Details</h4>
          <div className="cc-orgmodal-details-grid">
            <div className="cc-orgmodal-detail-row">
              <span className="cc-orgmodal-detail-label">Subscribed Since</span>
              <span className="cc-orgmodal-detail-value">{subscribedSince}</span>
            </div>
            <div className="cc-orgmodal-detail-row">
              <span className="cc-orgmodal-detail-label">Next Billing</span>
              <span className="cc-orgmodal-detail-value">{nextBilling}</span>
            </div>
            <div className="cc-orgmodal-detail-row">
              <span className="cc-orgmodal-detail-label">Country</span>
              <span className="cc-orgmodal-detail-value">{org.country}</span>
            </div>
            <div className="cc-orgmodal-detail-row">
              <span className="cc-orgmodal-detail-label">Active Modules</span>
              <span className="cc-orgmodal-detail-value">
                {org.modules.map(m => <span key={m} className="cc-orgmodal-mod-tag">{m}</span>)}
              </span>
            </div>
          </div>
        </div>

        {/* Overdue Alert */}
        {amountDue > 0 && org.overdueDays && (
          <div className="cc-orgmodal-overdue">
            <AlertTriangle size={16} />
            <span>${amountDue.toLocaleString()} overdue for {org.overdueDays} days</span>
          </div>
        )}

        {/* Recent Payments */}
        <div className="cc-orgmodal-section">
          <h4 className="cc-orgmodal-section-title">Recent Payments</h4>
          <div className="cc-orgmodal-payments">
            {mockPayments.map((p, i) => (
              <div key={i} className="cc-orgmodal-payment-row">
                <span className="cc-orgmodal-payment-date">{p.date}</span>
                <span className="cc-orgmodal-payment-amount">${p.amount.toLocaleString()}</span>
                <span className={`cc-orgmodal-payment-status cc-pay-${p.status.toLowerCase()}`}>{p.status}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="cc-orgmodal-footer">
          <button className="cc-orgmodal-secondary-btn" onClick={onClose}>Close</button>
          <a href={`/superadmin/organizations/${org.id}`} className="cc-orgmodal-primary-btn">
            <ExternalLink size={14} /> View Full Profile
          </a>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// 3D FLIP CARD
// ============================================================================

function FlipCard({ frontContent, backContent, delay = 0 }: {
  frontContent: React.ReactNode;
  backContent: React.ReactNode;
  delay?: number;
}) {
  const [flipped, setFlipped] = useState(false);

  return (
    <div
      className={`cc-flip-card ${flipped ? 'cc-flip-card--flipped' : ''}`}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="cc-flip-card-inner">
        <div className="cc-flip-card-front">{frontContent}</div>
        <div className="cc-flip-card-back">{backContent}</div>
      </div>
    </div>
  );
}

// ============================================================================
// DETAIL FLYOUT
// ============================================================================

function DetailFlyout({ org, onClose }: {
  org: MockOrg | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('platform');

  useEffect(() => {
    if (!org) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [org, onClose]);

  if (!org) return null;

  const planColors = { STARTER: '#3b82f6', PROFESSIONAL: '#8b5cf6', ENTERPRISE: '#f59e0b' };

  return (
    <div className="cc-flyout-overlay" onClick={onClose}>
      <div className="cc-flyout-panel" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="cc-flyout-header">
          <div>
            <h3 className="cc-flyout-title">{org.name}</h3>
            <span className="cc-flyout-plan-badge" style={{ background: `${planColors[org.plan]}20`, color: planColors[org.plan] }}>
              {org.plan}
            </span>
          </div>
          <button className="cc-flyout-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="cc-flyout-body">
          {/* Key Metrics */}
          <div className="cc-flyout-metrics">
            <div className="cc-flyout-metric">
              <span className="cc-flyout-metric-label">{t('dashboard.commandCenter.flyout.monthlyRevenue', 'Monthly Revenue')}</span>
              <span className="cc-flyout-metric-value">${org.monthlyRevenue.toLocaleString()}</span>
            </div>
            <div className="cc-flyout-metric">
              <span className="cc-flyout-metric-label">{t('dashboard.commandCenter.flyout.yearlyRevenue', 'Yearly Revenue')}</span>
              <span className="cc-flyout-metric-value">${org.yearlyRevenue.toLocaleString()}</span>
            </div>
            <div className="cc-flyout-metric">
              <span className="cc-flyout-metric-label">{t('dashboard.commandCenter.flyout.users', 'Users')}</span>
              <span className="cc-flyout-metric-value">{org.users}</span>
            </div>
            <div className="cc-flyout-metric">
              <span className="cc-flyout-metric-label">{t('dashboard.commandCenter.flyout.country', 'Country')}</span>
              <span className="cc-flyout-metric-value">{org.country}</span>
            </div>
          </div>

          {/* Status */}
          <div className="cc-flyout-section">
            <h4 className="cc-flyout-section-title">{t('dashboard.commandCenter.flyout.status', 'Status')}</h4>
            <div className="cc-flyout-status-row">
              <span className={`cc-flyout-status-dot cc-status-${org.status.toLowerCase()}`} />
              <span>{org.status}</span>
            </div>
            {org.overdueAmount && (
              <div className="cc-flyout-overdue-alert">
                <AlertTriangle size={14} />
                <span>${org.overdueAmount.toLocaleString()} overdue — {org.overdueDays} days</span>
              </div>
            )}
          </div>

          {/* Modules */}
          <div className="cc-flyout-section">
            <h4 className="cc-flyout-section-title">{t('dashboard.commandCenter.flyout.modules', 'Active Modules')}</h4>
            <div className="cc-flyout-modules">
              {org.modules.map(m => (
                <span key={m} className="cc-flyout-module-tag">{m}</span>
              ))}
            </div>
          </div>

          {/* Join date */}
          <div className="cc-flyout-section">
            <h4 className="cc-flyout-section-title">{t('dashboard.commandCenter.flyout.joined', 'Member Since')}</h4>
            <span className="cc-flyout-joined-date">
              {new Date(org.joinedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </div>

        <div className="cc-flyout-footer">
          <button className="cc-flyout-view-btn">
            <ExternalLink size={14} />
            {t('dashboard.commandCenter.flyout.viewDetails', 'View Full Profile')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// RANK MEDAL
// ============================================================================

const MEDALS = ['', '\u{1F947}', '\u{1F948}', '\u{1F949}'];

function RankBadge({ rank }: { rank: number }) {
  if (rank <= 3) {
    return <span className="cc-rank-medal">{MEDALS[rank]}</span>;
  }
  return <span className="cc-rank-number">#{rank}</span>;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DashboardPage() {
  const { t } = useTranslation('platform');
  const [selectedOrg, setSelectedOrg] = useState<MockOrg | null>(null);
  const [profileOrg, setProfileOrg] = useState<MockOrg | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return MOCK_ORGS.filter(o =>
      o.name.toLowerCase().includes(q) ||
      o.country.toLowerCase().includes(q) ||
      o.plan.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Donut drill-down state
  const [drilledSlice, setDrilledSlice] = useState<string | null>(null);
  const [hoveredSlice, setHoveredSlice] = useState<string | null>(null);
  const [isDrillCardsIn, setIsDrillCardsIn] = useState(false);
  const [isDrillExiting, setIsDrillExiting] = useState(false);
  const drillTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearDrillTimers = useCallback(() => {
    drillTimersRef.current.forEach(clearTimeout);
    drillTimersRef.current = [];
  }, []);

  const handleSliceClick = useCallback((label: string) => {
    if (drilledSlice) return;
    clearDrillTimers();
    setIsDrillExiting(false);
    setDrilledSlice(label);
    const t1 = setTimeout(() => setIsDrillCardsIn(true), 500);
    drillTimersRef.current = [t1];
  }, [drilledSlice, clearDrillTimers]);

  const handleDrillReturn = useCallback(() => {
    clearDrillTimers();
    setIsDrillExiting(true);
    const t = setTimeout(() => {
      setIsDrillExiting(false);
      setIsDrillCardsIn(false);
      setDrilledSlice(null);
    }, 400);
    drillTimersRef.current = [t];
  }, [clearDrillTimers]);

  useEffect(() => () => { clearDrillTimers(); }, [clearDrillTimers]);

  // Get orgs for drilled slice
  const drilledOrgs = useMemo(() => {
    if (!drilledSlice) return [];
    const statusMap: Record<string, string> = { 'Active': 'ACTIVE', 'Trial': 'TRIAL', 'Past Due': 'PAST_DUE', 'Cancelled': 'SUSPENDED' };
    const status = statusMap[drilledSlice];
    return MOCK_ORGS.filter(o => o.status === status);
  }, [drilledSlice]);

  // Donut arc computation
  const donutArcs = useMemo(() => {
    const total = SUBSCRIPTION_DISTRIBUTION.reduce((s, d) => s + d.count, 0);
    if (total === 0) return [];
    const cx = 90, cy = 90, outerR = 78, innerR = 50, gap = 2.5;
    let currentAngle = 0;
    return SUBSCRIPTION_DISTRIBUTION.map((d) => {
      const sweep = (d.count / total) * 360 - gap;
      const startAngle = currentAngle + gap / 2;
      const endAngle = startAngle + Math.max(sweep, 2);
      const midAngle = (startAngle + endAngle) / 2;
      const pushR = 6;
      const rad = ((midAngle - 90) * Math.PI) / 180;
      const dx = pushR * Math.cos(rad);
      const dy = pushR * Math.sin(rad);
      currentAngle += (d.count / total) * 360;
      return {
        ...d,
        path: donutSlicePath(cx, cy, outerR, innerR, startAngle, endAngle),
        pct: ((d.count / total) * 100).toFixed(0),
        dx, dy,
      };
    });
  }, []);

  // Sorted leaderboards
  const topYearly = useMemo(() =>
    [...MOCK_ORGS].filter(o => o.yearlyRevenue > 0).sort((a, b) => b.yearlyRevenue - a.yearlyRevenue).slice(0, 5),
    []
  );
  const topMonthly = useMemo(() =>
    [...MOCK_ORGS].filter(o => o.monthlyRevenue > 0).sort((a, b) => b.monthlyRevenue - a.monthlyRevenue).slice(0, 5),
    []
  );
  const topLatePay = useMemo(() =>
    [...MOCK_ORGS].filter(o => o.overdueAmount && o.overdueAmount > 0).sort((a, b) => (b.overdueAmount || 0) - (a.overdueAmount || 0)).slice(0, 5),
    []
  );

  const planColors: Record<string, string> = { STARTER: '#3b82f6', PROFESSIONAL: '#8b5cf6', ENTERPRISE: '#f59e0b' };

  const formatTimestamp = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('dashboard.time.justNow', 'Just now');
    if (diffMins < 60) return t('dashboard.time.minutesAgo', { count: diffMins, defaultValue: '{{count}}m ago' });
    if (diffHours < 24) return t('dashboard.time.hoursAgo', { count: diffHours, defaultValue: '{{count}}h ago' });
    return t('dashboard.time.daysAgo', { count: diffDays, defaultValue: '{{count}}d ago' });
  }, [t]);

  const activityTypeConfig: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
    ORG_CREATED: { icon: <CheckCircle size={14} />, color: 'var(--color-success)', bg: 'var(--color-success-light)' },
    ORG_SUSPENDED: { icon: <XCircle size={14} />, color: 'var(--color-error)', bg: 'var(--color-error-light)' },
    PAYMENT_RECEIVED: { icon: <DollarSign size={14} />, color: 'var(--color-info)', bg: 'var(--color-info-light)' },
    WITHDRAWAL_REQUESTED: { icon: <Clock size={14} />, color: 'var(--color-warning)', bg: 'var(--color-warning-light)' },
    USER_REGISTERED: { icon: <UserPlus size={14} />, color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.12)' },
  };

  const attentionTypeConfig: Record<string, { icon: React.ReactNode }> = {
    PENDING_REGISTRATION: { icon: <Users size={18} /> },
    FAILED_PAYMENT: { icon: <CreditCard size={18} /> },
    PENDING_WITHDRAWAL: { icon: <DollarSign size={18} /> },
    EXPIRING_TRIAL: { icon: <CalendarClock size={18} /> },
  };

  const urgencyConfig: Record<string, { border: string; glow: string; iconColor: string }> = {
    HIGH: { border: 'var(--color-error)', glow: 'rgba(239, 68, 68, 0.15)', iconColor: 'var(--color-error)' },
    MEDIUM: { border: 'var(--color-warning)', glow: 'rgba(245, 158, 11, 0.12)', iconColor: 'var(--color-warning)' },
    LOW: { border: 'var(--color-info)', glow: 'rgba(59, 130, 246, 0.10)', iconColor: 'var(--color-info)' },
  };

  return (
    <div className="cc-page">
      {/* ── HEADER ────────────────────────────────────────────────────── */}
      <div className="cc-header">
        <div className="cc-header-left">
          <div className="cc-header-icon-wrap">
            <Zap size={22} />
          </div>
          <div>
            <h1 className="cc-title">{t('dashboard.commandCenter.title', 'Command Center')}</h1>
            <p className="cc-subtitle">{t('dashboard.commandCenter.subtitle', 'Platform intelligence at a glance')}</p>
          </div>
        </div>
        <div className="cc-header-right">
          <button className="cc-refresh-btn" title={t('dashboard.refreshData', 'Refresh')}>
            <RefreshCw size={16} />
          </button>
          <div className="cc-search-box" ref={searchRef}>
            <Search size={14} className="cc-search-icon" />
            <input
              type="text"
              placeholder={t('dashboard.searchOrganizations', 'Search organizations...')}
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setSearchFocused(true); }}
              onFocus={() => setSearchFocused(true)}
              onKeyDown={e => { if (e.key === 'Escape') { setSearchFocused(false); setSearchQuery(''); } }}
            />
            {searchQuery && (
              <button className="cc-search-clear" onClick={() => { setSearchQuery(''); setSearchFocused(false); }}>
                <X size={12} />
              </button>
            )}
            {searchFocused && searchQuery.trim() && (
              <div className="cc-search-dropdown">
                {searchResults.length > 0 ? (
                  searchResults.map((org, i) => {
                    const planColors: Record<string, string> = { ENTERPRISE: '#f59e0b', PROFESSIONAL: '#3b82f6', STARTER: '#10b981' };
                    const statusColors: Record<string, string> = { ACTIVE: '#10b981', TRIAL: '#8b5cf6', PAST_DUE: '#ef4444', SUSPENDED: '#ef4444' };
                    return (
                      <div
                        key={org.id}
                        className="cc-search-result"
                        style={{ animationDelay: `${i * 30}ms` }}
                        onClick={() => { setSelectedOrg(org); setSearchFocused(false); setSearchQuery(''); }}
                      >
                        <div className="cc-search-result-avatar" style={{ background: `${planColors[org.plan]}18`, color: planColors[org.plan] }}>
                          <Building2 size={14} />
                        </div>
                        <div className="cc-search-result-info">
                          <span className="cc-search-result-name">{org.name}</span>
                          <span className="cc-search-result-meta">
                            <span style={{ color: planColors[org.plan] }}>{org.plan}</span>
                            <span className="cc-search-result-sep">&middot;</span>
                            <span style={{ color: statusColors[org.status] }}>{org.status.replace('_', ' ')}</span>
                            <span className="cc-search-result-sep">&middot;</span>
                            {org.country}
                          </span>
                        </div>
                        <span className="cc-search-result-revenue">${org.monthlyRevenue.toLocaleString()}/mo</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="cc-search-empty">
                    <Search size={16} style={{ opacity: 0.4 }} />
                    <span>No organizations found</span>
                  </div>
                )}
                {searchResults.length > 0 && (
                  <div className="cc-search-footer">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</div>
                )}
              </div>
            )}
          </div>
          <a href="/superadmin/organizations/new" className="cc-new-org-btn">
            <Plus size={15} />
            <span className="cc-new-org-text">{t('dashboard.newOrganization', 'New Org')}</span>
          </a>
        </div>
      </div>

      {/* ── HERO BENTO GRID (Metrics) ─────────────────────────────── */}
      <div className="cc-hero-grid">
        {/* Hero MRR Card (spans 2 rows) */}
        <div className="cc-hero-card">
          <div className="cc-hero-card-top">
            <div className="cc-hero-icon-wrap">
              <DollarSign size={22} />
            </div>
            <div className="cc-hero-trend cc-trend-positive">
              <TrendingUp size={13} /> +12.4%
            </div>
          </div>
          <div className="cc-hero-body">
            <div className="cc-hero-left">
              <div className="cc-hero-value">
                $<AnimatedCounter value={MOCK_STATS.totalMRR} />
              </div>
              <div className="cc-hero-label">{t('dashboard.commandCenter.mrr', 'Monthly Recurring Revenue')}</div>
              <div className="cc-hero-breakdown">
                <span className="cc-hero-active">${MOCK_STATS.activeMRR.toLocaleString()} {t('dashboard.stats.active', 'Active')}</span>
                <span className="cc-hero-sep">&middot;</span>
                <span className="cc-hero-trial">${MOCK_STATS.trialMRR.toLocaleString()} {t('dashboard.stats.trial', 'Trial')}</span>
              </div>
            </div>

            {/* Growth Pulse */}
            <div className="cc-growth-pulse">
            <div className="cc-growth-pulse-title">
              <Zap size={12} />
              <span>{t('dashboard.commandCenter.growthPulse', 'Growth Pulse')}</span>
              <span className="cc-growth-pulse-period">{t('dashboard.commandCenter.thisMonth', 'this month')}</span>
            </div>
            <div className="cc-growth-pulse-row">
              <div className="cc-growth-pill cc-growth-new">
                <ArrowUpRight size={12} />
                <span className="cc-growth-pill-label">New</span>
                <span className="cc-growth-pill-value">+${MOCK_STATS.newMRR}</span>
              </div>
              <div className="cc-growth-pill cc-growth-expansion">
                <TrendingUp size={12} />
                <span className="cc-growth-pill-label">Expansion</span>
                <span className="cc-growth-pill-value">+${MOCK_STATS.expansionMRR}</span>
              </div>
            </div>
            <div className="cc-growth-pulse-row">
              <div className="cc-growth-pill cc-growth-churned">
                <TrendingDown size={12} />
                <span className="cc-growth-pill-label">Churned</span>
                <span className="cc-growth-pill-value">-${MOCK_STATS.churnedMRR}</span>
              </div>
            </div>
            <div className="cc-growth-net">
              <span className="cc-growth-net-label">Net MRR Change</span>
              <span className="cc-growth-net-value cc-trend-positive">+${MOCK_STATS.newMRR + MOCK_STATS.expansionMRR - MOCK_STATS.churnedMRR}</span>
              <div className="cc-growth-net-bar">
                <div className="cc-growth-net-bar-fill" style={{ width: `${Math.min(((MOCK_STATS.newMRR + MOCK_STATS.expansionMRR) / (MOCK_STATS.newMRR + MOCK_STATS.expansionMRR + MOCK_STATS.churnedMRR)) * 100, 100)}%` }} />
              </div>
            </div>
          </div>
          </div>

        </div>

        {/* Satellite Cards */}
        <div className="cc-sat-card cc-sat-1">
          <div className="cc-sat-icon" style={{ background: 'rgba(139, 0, 0, 0.12)', color: 'var(--color-brand-primary)' }}>
            <Building2 size={18} />
          </div>
          <div className="cc-sat-info">
            <div className="cc-sat-value"><AnimatedCounter value={MOCK_STATS.totalOrgs} /></div>
            <div className="cc-sat-label">{t('dashboard.stats.totalOrganizations', 'Organizations')}</div>
          </div>
          <div className="cc-sat-trend cc-trend-positive"><TrendingUp size={11} /> +8%</div>
        </div>

        <div className="cc-sat-card cc-sat-2">
          <div className="cc-sat-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
            <Users size={18} />
          </div>
          <div className="cc-sat-info">
            <div className="cc-sat-value"><AnimatedCounter value={MOCK_STATS.totalUsers} /></div>
            <div className="cc-sat-label">{t('dashboard.stats.totalUsers', 'Total Users')}</div>
          </div>
          <div className="cc-sat-trend cc-trend-positive"><TrendingUp size={11} /> +15%</div>
        </div>

        <div className="cc-sat-card cc-sat-3">
          <div className="cc-sat-icon" style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' }}>
            <Briefcase size={18} />
          </div>
          <div className="cc-sat-info">
            <div className="cc-sat-value"><AnimatedCounter value={MOCK_STATS.totalPartners} /></div>
            <div className="cc-sat-label">{t('dashboard.stats.activePartners', 'Partners')}</div>
          </div>
          <div className="cc-sat-trend cc-trend-positive"><TrendingUp size={11} /> +6%</div>
        </div>

        <div className="cc-sat-card cc-sat-4">
          <div className="cc-sat-icon" style={{ background: 'rgba(14, 166, 141, 0.12)', color: '#0EA68D' }}>
            <Activity size={18} />
          </div>
          <div className="cc-sat-info">
            <div className="cc-sat-value"><AnimatedCounter value={MOCK_STATS.activeSessions} /></div>
            <div className="cc-sat-label">{t('dashboard.commandCenter.liveSessions', 'Live Sessions')}</div>
          </div>
          <div className="cc-sat-live-dot" />
        </div>
      </div>

      {/* ── INTELLIGENCE GRID (Charts) ────────────────────────────── */}
      <div className="cc-intel-grid">
        {/* Revenue Trend */}
        <div className="cc-intel-card cc-intel-trend">
          <div className="cc-intel-card-header">
            <div className="cc-intel-card-title-wrap">
              <BarChart3 size={16} className="cc-intel-icon" />
              <h3 className="cc-intel-card-title">{t('dashboard.commandCenter.revenueTrend', 'Revenue Trend')}</h3>
            </div>
            <span className="cc-intel-card-period">{t('dashboard.commandCenter.last12Months', 'Last 12 Months')}</span>
          </div>
          <div className="cc-area-chart-wrap">
            <AreaChart data={MOCK_REVENUE_TREND} />
          </div>
        </div>

        {/* Subscription Distribution — Animated Donut with Drill-Down */}
        <div className="cc-intel-card cc-intel-donut">
          <div className="cc-intel-card-header">
            <div className="cc-intel-card-title-wrap">
              <PieChart size={16} className="cc-intel-icon" />
              <h3 className="cc-intel-card-title">{t('dashboard.commandCenter.subscriptionDist', 'Subscriptions')}</h3>
            </div>
            {drilledSlice && (
              <button className="cc-donut-back-btn" onClick={handleDrillReturn}>
                <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back
              </button>
            )}
          </div>
          <div className="cc-donut-wrap">
            {/* SVG Donut */}
            <div className="cc-donut-svg-container cc-donut-animated">
              <svg width="180" height="180" viewBox="0 0 180 180" overflow="visible" className="cc-donut-svg">
                {donutArcs.map((arc, i) => (
                  <path
                    key={arc.label}
                    d={arc.path}
                    fill={arc.color}
                    className={[
                      'cc-donut-segment',
                      drilledSlice === arc.label ? 'cc-seg-drilled' : '',
                      drilledSlice && drilledSlice !== arc.label ? 'cc-seg-vanish' : '',
                      !drilledSlice && hoveredSlice === arc.label ? 'cc-seg-hovered' : '',
                      !drilledSlice && hoveredSlice && hoveredSlice !== arc.label ? 'cc-seg-dimmed' : '',
                    ].filter(Boolean).join(' ')}
                    style={{
                      transform: !drilledSlice && hoveredSlice === arc.label
                        ? `translate(${arc.dx}px, ${arc.dy}px)`
                        : 'translate(0px, 0px)',
                    }}
                    onClick={() => handleSliceClick(arc.label)}
                    onMouseEnter={() => !drilledSlice && setHoveredSlice(arc.label)}
                    onMouseLeave={() => !drilledSlice && setHoveredSlice(null)}
                  />
                ))}
              </svg>
              {drilledSlice && <div className="cc-donut-fog" />}
              {/* Center text */}
              <div className="cc-donut-center-overlay">
                {drilledSlice ? (
                  <>
                    <span className="cc-donut-hov-name">{drilledSlice}</span>
                    <span className="cc-donut-hov-pct">{donutArcs.find(a => a.label === drilledSlice)?.pct}%</span>
                    <span className="cc-donut-hov-count">{drilledOrgs.length} orgs</span>
                  </>
                ) : hoveredSlice ? (
                  <>
                    <span className="cc-donut-hov-name">{hoveredSlice}</span>
                    <span className="cc-donut-hov-pct">{donutArcs.find(a => a.label === hoveredSlice)?.pct}%</span>
                    <span className="cc-donut-hov-count">{donutArcs.find(a => a.label === hoveredSlice)?.count} orgs</span>
                  </>
                ) : (
                  <>
                    <span className="cc-donut-center-total">{SUBSCRIPTION_DISTRIBUTION.reduce((s, d) => s + d.count, 0)}</span>
                    <span className="cc-donut-center-sub">Orgs</span>
                  </>
                )}
              </div>
            </div>

            {/* Right side: Legend OR Drilled Org List */}
            {drilledSlice ? (
              <div className={`cc-drill-panel ${isDrillCardsIn ? 'cc-drill-in' : ''} ${isDrillExiting ? 'cc-drill-exit' : ''}`}>
                <div className="cc-drill-list">
                  {drilledOrgs.map((org, i) => (
                    <div key={org.id} className="cc-drill-org-row" style={{ animationDelay: `${i * 60}ms` }}>
                      <div className="cc-drill-org-info">
                        <span className="cc-drill-org-name">{org.name}</span>
                        <span className="cc-drill-org-sub">${org.monthlyRevenue.toLocaleString()}/mo &middot; {org.users} users</span>
                      </div>
                      <button className="cc-drill-view-btn" onClick={() => setProfileOrg(org)}>
                        <Eye size={13} /> View
                      </button>
                    </div>
                  ))}
                  {drilledOrgs.length === 0 && (
                    <div className="cc-drill-empty">No organizations</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="cc-donut-legend">
                {donutArcs.map((d, i) => (
                  <div
                    key={i}
                    className={`cc-donut-legend-row ${hoveredSlice === d.label ? 'cc-legend-hov' : ''}`}
                    onMouseEnter={() => setHoveredSlice(d.label)}
                    onMouseLeave={() => setHoveredSlice(null)}
                    onClick={() => handleSliceClick(d.label)}
                  >
                    <span className="cc-donut-dot" style={{ background: d.color }} />
                    <span className="cc-donut-legend-label">{d.label}</span>
                    <span className="cc-donut-legend-value">{d.count}</span>
                    <span className="cc-donut-legend-pct">{d.pct}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── LEADERBOARD ZONE ──────────────────────────────────────── */}
      <div className="cc-leaders-grid">
        {/* Top 5 Yearly Revenue */}
        <div className="cc-leader-card">
          <div className="cc-leader-header">
            <Crown size={16} className="cc-leader-icon" style={{ color: '#f59e0b' }} />
            <h3 className="cc-leader-title">{t('dashboard.commandCenter.topYearly', 'Top 5 — Yearly Revenue')}</h3>
          </div>
          <div className="cc-leader-list">
            {topYearly.map((org, i) => (
              <FlipCard
                key={org.id}
                delay={0.06 * i}
                frontContent={
                  <div className="cc-leader-item" onClick={() => setSelectedOrg(org)}>
                    <RankBadge rank={i + 1} />
                    <div className="cc-leader-item-info">
                      <span className="cc-leader-item-name">{org.name}</span>
                      <span className="cc-leader-item-plan" style={{ color: planColors[org.plan] }}>{org.plan}</span>
                    </div>
                    <span className="cc-leader-item-amount">${org.yearlyRevenue.toLocaleString()}</span>
                  </div>
                }
                backContent={
                  <div className="cc-leader-item-back" onClick={() => setSelectedOrg(org)}>
                    <div className="cc-leader-back-row"><span>Users:</span><strong>{org.users}</strong></div>
                    <div className="cc-leader-back-row"><span>Monthly:</span><strong>${org.monthlyRevenue.toLocaleString()}</strong></div>
                    <div className="cc-leader-back-row"><span>Country:</span><strong>{org.country}</strong></div>
                    <div className="cc-leader-back-row"><span>Modules:</span><strong>{org.modules.length}</strong></div>
                  </div>
                }
              />
            ))}
          </div>
        </div>

        {/* Top 5 Monthly Revenue */}
        <div className="cc-leader-card">
          <div className="cc-leader-header">
            <TrendingUp size={16} className="cc-leader-icon" style={{ color: '#10b981' }} />
            <h3 className="cc-leader-title">{t('dashboard.commandCenter.topMonthly', 'Top 5 — Monthly Revenue')}</h3>
          </div>
          <div className="cc-leader-list">
            {topMonthly.map((org, i) => (
              <FlipCard
                key={org.id}
                delay={0.06 * i}
                frontContent={
                  <div className="cc-leader-item" onClick={() => setSelectedOrg(org)}>
                    <RankBadge rank={i + 1} />
                    <div className="cc-leader-item-info">
                      <span className="cc-leader-item-name">{org.name}</span>
                      <span className="cc-leader-item-plan" style={{ color: planColors[org.plan] }}>{org.plan}</span>
                    </div>
                    <span className="cc-leader-item-amount">${org.monthlyRevenue.toLocaleString()}</span>
                  </div>
                }
                backContent={
                  <div className="cc-leader-item-back" onClick={() => setSelectedOrg(org)}>
                    <div className="cc-leader-back-row"><span>Yearly:</span><strong>${org.yearlyRevenue.toLocaleString()}</strong></div>
                    <div className="cc-leader-back-row"><span>Users:</span><strong>{org.users}</strong></div>
                    <div className="cc-leader-back-row"><span>Country:</span><strong>{org.country}</strong></div>
                    <div className="cc-leader-back-row"><span>Joined:</span><strong>{new Date(org.joinedAt).toLocaleDateString('en', { month: 'short', year: 'numeric' })}</strong></div>
                  </div>
                }
              />
            ))}
          </div>
        </div>

        {/* Top 5 Late Payments */}
        <div className="cc-leader-card cc-leader-card-danger">
          <div className="cc-leader-header">
            <AlertTriangle size={16} className="cc-leader-icon" style={{ color: 'var(--color-error)' }} />
            <h3 className="cc-leader-title">{t('dashboard.commandCenter.topLatePay', 'Top 5 — Late Payments')}</h3>
          </div>
          <div className="cc-leader-list">
            {topLatePay.map((org, i) => {
              const severity = (org.overdueDays || 0) > 30 ? 'critical' : (org.overdueDays || 0) > 14 ? 'warning' : 'mild';
              return (
                <div
                  key={org.id}
                  className={`cc-late-item cc-late-${severity}`}
                  onClick={() => setSelectedOrg(org)}
                  style={{ animationDelay: `${0.08 * i + 0.4}s` }}
                >
                  <div className="cc-late-rank">#{i + 1}</div>
                  <div className="cc-late-info">
                    <span className="cc-late-name">{org.name}</span>
                    <span className="cc-late-days">
                      {org.overdueDays} {t('dashboard.commandCenter.daysOverdue', 'days overdue')}
                    </span>
                  </div>
                  <div className="cc-late-amount">${org.overdueAmount?.toLocaleString()}</div>
                  {severity === 'critical' && <span className="cc-late-pulse" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── ATTENTION + ACTIVITY ──────────────────────────────────── */}
      <div className="cc-bottom-grid">
        {/* Attention Required */}
        <div className="cc-bottom-card">
          <div className="cc-bottom-card-header">
            <Shield size={16} />
            <h3 className="cc-bottom-card-title">{t('dashboard.attentionRequired.title', 'Attention Required')}</h3>
          </div>
          <div className="cc-attention-list">
            {MOCK_ATTENTION.map((item, i) => {
              const uc = urgencyConfig[item.urgency];
              const tc = attentionTypeConfig[item.type];
              return (
                <a
                  key={i}
                  href={item.route}
                  className={`cc-attention-item ${item.urgency === 'HIGH' ? 'cc-attention-pulse' : ''}`}
                  style={{ borderLeftColor: uc.border, animationDelay: `${0.06 * i + 0.6}s` }}
                >
                  <div className="cc-attention-icon" style={{ color: uc.iconColor }}>
                    {tc.icon}
                  </div>
                  <div className="cc-attention-content">
                    <div className="cc-attention-title">{item.title}</div>
                    <div className="cc-attention-desc">{item.description}</div>
                  </div>
                  <div className="cc-attention-count">{item.count}</div>
                  <ChevronRight size={14} className="cc-attention-arrow" style={{ color: uc.iconColor }} />
                </a>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="cc-bottom-card">
          <div className="cc-bottom-card-header">
            <Activity size={16} />
            <h3 className="cc-bottom-card-title">{t('dashboard.recentActivity.title', 'Recent Activity')}</h3>
          </div>
          <div className="cc-activity-list">
            {MOCK_ACTIVITIES.map((item, i) => {
              const cfg = activityTypeConfig[item.type] || { icon: <Clock size={14} />, color: 'var(--color-text-muted)', bg: 'var(--color-bg-hover)' };
              return (
                <div key={item.id} className="cc-activity-item" style={{ animationDelay: `${0.04 * i + 0.7}s` }}>
                  <div className="cc-activity-orb" style={{ background: cfg.bg, color: cfg.color }}>
                    {cfg.icon}
                  </div>
                  <div className="cc-activity-content">
                    <span className="cc-activity-name">{item.entityName}</span>
                    <span className="cc-activity-desc">{item.description}</span>
                  </div>
                  <span className="cc-activity-time">{formatTimestamp(item.createdAt)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── DETAIL FLYOUT ─────────────────────────────────────────── */}
      <DetailFlyout org={selectedOrg} onClose={() => setSelectedOrg(null)} />

      {/* ── ORG PROFILE MODAL ─────────────────────────────────────── */}
      <OrgProfileModal org={profileOrg} onClose={() => setProfileOrg(null)} />

      {/* ── STYLES ─────────────────────────────────────────────────── */}
      <style>{commandCenterStyles}</style>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const commandCenterStyles = `
  /* ── Page Container ──────────────────────────────────────────── */
  .cc-page {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    padding: 1.5rem 2rem;
    width: 100%;
    box-sizing: border-box;
    min-height: 100%;
  }

  /* ── Header ──────────────────────────────────────────────────── */
  .cc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    animation: cc-fade-down 0.4s cubic-bezier(0.22,1,0.36,1) both;
    position: relative;
    z-index: 50;
  }
  .cc-header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .cc-header-icon-wrap {
    width: 42px;
    height: 42px;
    border-radius: var(--radius-lg);
    background: color-mix(in srgb, var(--color-brand-primary) 12%, var(--color-bg-secondary));
    color: var(--color-brand-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .cc-title {
    font-family: var(--font-heading);
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--color-text-primary);
    margin: 0;
    line-height: 1.2;
  }
  .cc-subtitle {
    font-size: 0.8rem;
    color: var(--color-text-muted);
    margin: 0;
  }
  .cc-header-right {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }
  .cc-refresh-btn {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    color: var(--color-text-secondary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }
  .cc-refresh-btn:hover {
    border-color: var(--color-brand-primary);
    color: var(--color-brand-primary);
  }
  .cc-search-box {
    position: relative;
    width: 220px;
  }
  .cc-search-box input {
    width: 100%;
    height: 36px;
    padding: 0 1.75rem 0 2rem;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
    font-size: 0.8rem;
    color: var(--color-text-primary);
    transition: border-color 0.15s ease;
  }
  .cc-search-box input:focus {
    outline: none;
    border-color: var(--color-brand-primary);
  }
  .cc-search-icon {
    position: absolute;
    left: 0.6rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    pointer-events: none;
  }
  .cc-search-clear {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: var(--radius-sm);
    background: var(--color-bg-tertiary);
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.12s ease;
  }
  .cc-search-clear:hover {
    background: var(--color-error-light, color-mix(in srgb, var(--color-error) 12%, var(--color-bg-secondary)));
    color: var(--color-error);
  }

  /* Search dropdown */
  .cc-search-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    width: 340px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
    box-shadow: 0 12px 40px rgba(0,0,0,0.25), 0 4px 12px rgba(0,0,0,0.1);
    z-index: 500;
    overflow: hidden;
    animation: cc-search-drop 0.2s cubic-bezier(0.16,1,0.3,1) both;
  }
  @keyframes cc-search-drop {
    from { opacity: 0; transform: translateY(-6px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .cc-search-result {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    transition: background 0.12s ease;
    animation: cc-card-up 0.2s cubic-bezier(0.22,1,0.36,1) both;
  }
  .cc-search-result:hover {
    background: var(--color-bg-hover, color-mix(in srgb, var(--color-bg-tertiary) 50%, var(--color-bg-secondary)));
  }
  .cc-search-result + .cc-search-result {
    border-top: 1px solid var(--color-border-subtle);
  }
  .cc-search-result-avatar {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .cc-search-result-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .cc-search-result-name {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--color-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cc-search-result-meta {
    font-size: 0.62rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
  .cc-search-result-sep {
    color: var(--color-text-muted);
    font-weight: 400;
  }
  .cc-search-result-revenue {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--color-text-secondary);
    flex-shrink: 0;
  }
  .cc-search-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    padding: 1.25rem;
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }
  .cc-search-footer {
    padding: 0.375rem 0.75rem;
    font-size: 0.65rem;
    color: var(--color-text-muted);
    border-top: 1px solid var(--color-border-subtle);
    text-align: right;
  }
  .cc-new-org-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 1rem;
    background: var(--color-brand-primary);
    color: var(--button-primary-text, #fff);
    border-radius: var(--radius-md);
    font-size: 0.8rem;
    font-weight: 600;
    text-decoration: none;
    transition: opacity 0.15s ease;
  }
  .cc-new-org-btn:hover { opacity: 0.9; }

  /* ── Hero Bento Grid ─────────────────────────────────────────── */
  .cc-hero-grid {
    display: grid;
    grid-template-columns: 1.8fr 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 0.875rem;
  }

  /* Hero MRR Card (spans 2 rows) */
  .cc-hero-card {
    grid-row: 1 / 3;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    padding: 1.5rem;
    position: relative;
    overflow: hidden;
    box-shadow: var(--shadow-sm);
    animation: cc-card-up 0.45s cubic-bezier(0.22,1,0.36,1) 0.04s both;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    display: flex;
    flex-direction: column;
  }
  .cc-hero-card:hover {
    border-color: color-mix(in srgb, var(--color-brand-primary) 25%, var(--color-border-subtle));
    box-shadow: var(--shadow-md);
  }
  .cc-hero-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--color-brand-primary), color-mix(in srgb, var(--color-brand-primary) 40%, #10b981));
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }
  .cc-hero-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }
  .cc-hero-icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-md);
    background: color-mix(in srgb, #10b981 12%, var(--color-bg-secondary));
    color: #10b981;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cc-hero-trend {
    font-size: 0.75rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.2rem;
  }
  .cc-trend-positive { color: #10b981; }
  .cc-trend-negative { color: var(--color-error); }
  .cc-hero-body {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex: 1;
  }
  .cc-hero-left {
    flex-shrink: 0;
  }
  .cc-hero-value {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--color-text-primary);
    line-height: 1.1;
    margin-bottom: 0.25rem;
  }
  .cc-hero-label {
    font-size: 0.8rem;
    color: var(--color-text-muted);
    margin-bottom: 0.75rem;
  }
  .cc-hero-breakdown {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
  }
  .cc-hero-active { color: #10b981; font-weight: 600; }
  .cc-hero-trial { color: var(--color-warning); font-weight: 600; }
  .cc-hero-sep { color: var(--color-text-muted); }
  .cc-hero-donut-wrap {
    position: absolute;
    bottom: 1.25rem;
    right: 1.5rem;
    opacity: 0.75;
  }

  /* Growth Pulse */
  .cc-growth-pulse {
    flex: 1;
    min-width: 0;
    padding: 0.75rem;
    background: color-mix(in srgb, var(--color-bg-tertiary) 60%, var(--color-bg-secondary));
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
    animation: cc-card-up 0.5s cubic-bezier(0.22,1,0.36,1) 0.2s both;
  }
  .cc-growth-pulse-title {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--color-text-secondary);
    margin-bottom: 0.625rem;
  }
  .cc-growth-pulse-period {
    font-weight: 400;
    color: var(--color-text-muted);
    margin-left: auto;
    font-size: 0.65rem;
  }
  .cc-growth-pulse-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.625rem;
  }
  .cc-growth-pill {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.4rem 0.5rem;
    border-radius: var(--radius-sm);
    font-size: 0.68rem;
    font-weight: 600;
    border: 1px solid transparent;
    white-space: nowrap;
  }
  .cc-growth-pill-label {
    flex: 1;
    font-weight: 500;
    opacity: 0.8;
  }
  .cc-growth-pill-value {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-weight: 700;
    font-size: 0.72rem;
  }
  .cc-growth-new {
    background: color-mix(in srgb, #22c55e 10%, var(--color-bg-secondary));
    border-color: color-mix(in srgb, #22c55e 20%, transparent);
    color: #22c55e;
  }
  .cc-growth-expansion {
    background: color-mix(in srgb, #3b82f6 10%, var(--color-bg-secondary));
    border-color: color-mix(in srgb, #3b82f6 20%, transparent);
    color: #3b82f6;
  }
  .cc-growth-churned {
    background: color-mix(in srgb, #ef4444 10%, var(--color-bg-secondary));
    border-color: color-mix(in srgb, #ef4444 20%, transparent);
    color: #ef4444;
  }
  .cc-growth-net {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--color-border-subtle);
  }
  .cc-growth-net-label {
    font-size: 0.68rem;
    color: var(--color-text-muted);
    white-space: nowrap;
  }
  .cc-growth-net-value {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.82rem;
    font-weight: 700;
    white-space: nowrap;
  }
  .cc-growth-net-bar {
    flex: 1;
    height: 4px;
    background: color-mix(in srgb, var(--color-error) 20%, var(--color-bg-tertiary));
    border-radius: 2px;
    overflow: hidden;
    min-width: 40px;
  }
  .cc-growth-net-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #22c55e, #10b981);
    border-radius: 2px;
    animation: cc-bar-grow 0.8s cubic-bezier(0.22,1,0.36,1) 0.6s both;
  }
  @keyframes cc-bar-grow {
    from { width: 0 !important; }
  }

  /* Satellite Cards */
  .cc-sat-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    padding: 1rem 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    box-shadow: var(--shadow-sm);
    transition: all 0.2s ease;
    position: relative;
  }
  .cc-sat-card:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
    border-color: var(--color-border-default);
  }
  .cc-sat-1 { animation: cc-card-up 0.45s cubic-bezier(0.22,1,0.36,1) 0.10s both; }
  .cc-sat-2 { animation: cc-card-up 0.45s cubic-bezier(0.22,1,0.36,1) 0.16s both; }
  .cc-sat-3 { animation: cc-card-up 0.45s cubic-bezier(0.22,1,0.36,1) 0.22s both; }
  .cc-sat-4 { animation: cc-card-up 0.45s cubic-bezier(0.22,1,0.36,1) 0.28s both; }
  .cc-sat-icon {
    width: 38px;
    height: 38px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .cc-sat-info { flex: 1; min-width: 0; }
  .cc-sat-value {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--color-text-primary);
    line-height: 1.2;
  }
  .cc-sat-label {
    font-size: 0.72rem;
    color: var(--color-text-muted);
  }
  .cc-sat-trend {
    font-size: 0.68rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
  }
  .cc-sat-live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;
    flex-shrink: 0;
    animation: cc-pulse-dot 2s ease-in-out infinite;
  }

  /* ── Intelligence Grid ───────────────────────────────────────── */
  .cc-intel-grid {
    display: grid;
    grid-template-columns: 1.6fr 1fr;
    gap: 0.875rem;
  }
  .cc-intel-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    padding: 1.25rem;
    box-shadow: var(--shadow-sm);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .cc-intel-card:hover {
    border-color: var(--color-border-default);
    box-shadow: var(--shadow-md);
  }
  .cc-intel-trend { animation: cc-card-up 0.5s cubic-bezier(0.22,1,0.36,1) 0.32s both; }
  .cc-intel-donut { animation: cc-card-up 0.5s cubic-bezier(0.22,1,0.36,1) 0.36s both; }
  .cc-intel-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }
  .cc-intel-card-title-wrap {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .cc-intel-icon { color: var(--color-text-muted); }
  .cc-intel-card-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
  }
  .cc-intel-card-period {
    font-size: 0.7rem;
    color: var(--color-text-muted);
    padding: 0.2rem 0.6rem;
    background: var(--color-bg-tertiary);
    border-radius: var(--radius-sm);
  }
  .cc-area-chart-wrap {
    width: 100%;
    height: 180px;
  }
  .cc-area-chart { overflow: visible; }
  .cc-chart-label {
    font-size: 9px;
    fill: var(--color-text-muted);
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
  }
  .cc-chart-tooltip-text {
    font-size: 10px;
    fill: var(--color-text-primary);
    font-weight: 600;
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
  }
  .cc-area-line {
    stroke-dasharray: 1200;
    stroke-dashoffset: 1200;
    animation: cc-draw-line 1.2s ease-out 0.5s forwards;
  }
  .cc-area-fill {
    opacity: 0;
    animation: cc-fade-in 0.8s ease 1.0s forwards;
  }

  /* ── Animated Donut ─────────────────────────────────────────── */
  .cc-donut-wrap {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    justify-content: center;
    padding: 0.5rem 0;
    position: relative;
  }

  /* Animated entrance */
  .cc-donut-svg-container {
    flex-shrink: 0;
    position: relative;
  }
  .cc-donut-animated {
    animation: cc-donut-reveal 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s both;
  }
  @keyframes cc-donut-reveal {
    from { opacity: 0; transform: scale(0) rotate(-180deg); }
    to   { opacity: 1; transform: scale(1) rotate(0deg); }
  }
  .cc-donut-svg { flex-shrink: 0; }

  /* Donut segment interaction states */
  .cc-donut-segment {
    cursor: pointer;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
                filter 0.25s ease,
                opacity 0.3s ease;
    transform-origin: center;
  }
  .cc-seg-hovered {
    filter: brightness(1.15) drop-shadow(0 0 6px currentColor);
  }
  .cc-seg-dimmed {
    opacity: 0.4;
    filter: saturate(0.5);
  }
  .cc-seg-drilled {
    filter: brightness(1.2) drop-shadow(0 0 10px currentColor);
    transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1),
                filter 0.5s ease;
  }
  .cc-seg-vanish {
    opacity: 0;
    transform: scale(0.3);
    transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.55, 0, 1, 0.45);
    pointer-events: none;
  }

  /* Fog overlay on drill */
  .cc-donut-fog {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(circle, transparent 40%, color-mix(in srgb, var(--color-bg-secondary) 70%, transparent) 100%);
    pointer-events: none;
    animation: cc-fog-cover 0.5s ease both;
    z-index: 1;
  }
  @keyframes cc-fog-cover {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* Center overlay text */
  .cc-donut-center-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 2;
    text-align: center;
  }
  .cc-donut-hov-name {
    font-size: 11px;
    font-weight: 700;
    color: var(--color-text-primary);
    line-height: 1.2;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cc-donut-hov-pct {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 18px;
    font-weight: 700;
    color: var(--color-text-primary);
    line-height: 1.2;
  }
  .cc-donut-hov-count {
    font-size: 10px;
    color: var(--color-text-muted);
  }
  .cc-donut-center-total {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }
  .cc-donut-center-sub {
    font-size: 10px;
    color: var(--color-text-muted);
  }

  /* Back button (drill mode) */
  .cc-donut-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-sm);
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--color-text-secondary);
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }
  .cc-donut-back-btn:hover {
    color: var(--color-brand-primary);
    border-color: var(--color-brand-primary);
  }

  /* ── Drill Panel (right side list) ───────────────────────────── */
  .cc-drill-panel {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    opacity: 0;
    transform: translateX(20px);
    transition: opacity 0.35s ease, transform 0.35s ease;
    pointer-events: none;
    min-width: 180px;
    max-width: 220px;
  }
  .cc-drill-panel.cc-drill-in {
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
  .cc-drill-panel.cc-drill-exit {
    opacity: 0;
    transform: translateX(20px);
  }
  .cc-drill-list {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    max-height: 200px;
    overflow-y: auto;
    padding-right: 4px;
  }
  .cc-drill-list::-webkit-scrollbar { width: 3px; }
  .cc-drill-list::-webkit-scrollbar-thumb { background: var(--color-border-subtle); border-radius: 3px; }
  .cc-drill-org-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.4rem 0.6rem;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
    animation: cc-card-up 0.35s cubic-bezier(0.22,1,0.36,1) both;
    transition: border-color 0.15s ease;
  }
  .cc-drill-org-row:hover {
    border-color: color-mix(in srgb, var(--color-brand-primary) 35%, var(--color-border-subtle));
  }
  .cc-drill-org-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  }
  .cc-drill-org-name {
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--color-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cc-drill-org-sub {
    font-size: 0.62rem;
    color: var(--color-text-muted);
  }
  .cc-drill-view-btn {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 8px;
    background: color-mix(in srgb, var(--color-brand-primary) 10%, var(--color-bg-secondary));
    color: var(--color-brand-primary);
    border: 1px solid color-mix(in srgb, var(--color-brand-primary) 20%, transparent);
    border-radius: var(--radius-sm);
    font-size: 0.62rem;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }
  .cc-drill-view-btn:hover {
    background: color-mix(in srgb, var(--color-brand-primary) 18%, var(--color-bg-secondary));
  }
  .cc-drill-empty {
    font-size: 0.72rem;
    color: var(--color-text-muted);
    text-align: center;
    padding: 1rem;
  }

  /* Legend hover state */
  .cc-legend-hov {
    opacity: 1 !important;
    background: color-mix(in srgb, var(--color-brand-primary) 6%, transparent);
    border-radius: var(--radius-sm);
    padding: 0.15rem 0.35rem;
    margin: -0.15rem -0.35rem;
  }

  /* Donut legend */
  .cc-donut-legend {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .cc-donut-legend-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.78rem;
    cursor: pointer;
    transition: opacity 0.15s ease, background 0.15s ease;
  }
  .cc-donut-legend-row:hover { opacity: 0.8; }
  .cc-donut-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .cc-donut-legend-label {
    color: var(--color-text-secondary);
    flex: 1;
  }
  .cc-donut-legend-value {
    font-weight: 600;
    color: var(--color-text-primary);
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
  }
  .cc-donut-legend-pct {
    font-size: 0.7rem;
    color: var(--color-text-muted);
    width: 28px;
    text-align: right;
  }

  /* ── Org Profile Modal ───────────────────────────────────────── */
  .cc-orgmodal-overlay {
    position: fixed;
    inset: 0;
    z-index: 300;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: cc-overlay-in 0.2s ease both;
    padding: 1.5rem;
  }
  .cc-orgmodal {
    width: 520px;
    max-width: 100%;
    max-height: 88vh;
    overflow-y: auto;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-xl, 16px);
    box-shadow: 0 24px 64px rgba(0,0,0,0.25);
    animation: cc-orgmodal-in 0.35s cubic-bezier(0.22,1,0.36,1) both;
  }
  @keyframes cc-orgmodal-in {
    from { opacity: 0; transform: scale(0.92) translateY(12px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  /* Header */
  .cc-orgmodal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1.5rem 1.5rem 1rem;
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .cc-orgmodal-header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    min-width: 0;
  }
  .cc-orgmodal-avatar {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-lg);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .cc-orgmodal-name {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--color-text-primary);
    margin: 0;
    line-height: 1.2;
  }
  .cc-orgmodal-badges {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.25rem;
    flex-wrap: wrap;
  }
  .cc-orgmodal-plan-badge {
    font-size: 0.62rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 9999px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .cc-orgmodal-status-badge {
    font-size: 0.62rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    text-transform: capitalize;
  }
  .cc-orgmodal-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .cc-orgmodal-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.12s ease;
    flex-shrink: 0;
  }
  .cc-orgmodal-close:hover {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
  }

  /* Metrics grid */
  .cc-orgmodal-metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.625rem;
    padding: 1rem 1.5rem;
  }
  .cc-orgmodal-metric {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem;
    background: var(--color-bg-tertiary);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-subtle);
  }
  .cc-orgmodal-metric-icon {
    flex-shrink: 0;
  }
  .cc-orgmodal-metric-label {
    font-size: 0.65rem;
    color: var(--color-text-muted);
    display: block;
    line-height: 1.2;
  }
  .cc-orgmodal-metric-value {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 1rem;
    font-weight: 700;
    color: var(--color-text-primary);
    display: block;
    line-height: 1.2;
  }

  /* Detail sections */
  .cc-orgmodal-section {
    padding: 0 1.5rem 1rem;
  }
  .cc-orgmodal-section-title {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 0.625rem;
  }
  .cc-orgmodal-details-grid {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .cc-orgmodal-detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    background: var(--color-bg-primary);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-subtle);
  }
  .cc-orgmodal-detail-label {
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }
  .cc-orgmodal-detail-value {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .cc-orgmodal-mod-tag {
    font-size: 0.62rem;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: var(--radius-sm);
    background: color-mix(in srgb, var(--color-brand-primary) 10%, var(--color-bg-secondary));
    color: var(--color-brand-primary);
  }

  /* Overdue alert */
  .cc-orgmodal-overdue {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.5rem 1.5rem 0.75rem;
    padding: 0.625rem 0.875rem;
    background: color-mix(in srgb, var(--color-error) 8%, var(--color-bg-secondary));
    border: 1px solid color-mix(in srgb, var(--color-error) 20%, transparent);
    border-radius: var(--radius-md);
    color: var(--color-error);
    font-size: 0.75rem;
    font-weight: 600;
  }

  /* Payments table */
  .cc-orgmodal-payments {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }
  .cc-orgmodal-payment-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    background: var(--color-bg-primary);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-subtle);
    font-size: 0.75rem;
  }
  .cc-orgmodal-payment-date {
    color: var(--color-text-muted);
    width: 80px;
    flex-shrink: 0;
  }
  .cc-orgmodal-payment-amount {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-weight: 700;
    color: var(--color-text-primary);
    flex: 1;
    text-align: center;
  }
  .cc-orgmodal-payment-status {
    font-weight: 600;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 8px;
    border-radius: 9999px;
    flex-shrink: 0;
  }
  .cc-pay-paid {
    background: color-mix(in srgb, #10b981 12%, var(--color-bg-secondary));
    color: #10b981;
  }
  .cc-pay-pending {
    background: color-mix(in srgb, var(--color-warning) 12%, var(--color-bg-secondary));
    color: var(--color-warning);
  }
  .cc-pay-overdue {
    background: color-mix(in srgb, var(--color-error) 12%, var(--color-bg-secondary));
    color: var(--color-error);
  }

  /* Footer */
  .cc-orgmodal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.625rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--color-border-subtle);
  }
  .cc-orgmodal-secondary-btn {
    padding: 0.5rem 1rem;
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all 0.12s ease;
  }
  .cc-orgmodal-secondary-btn:hover {
    border-color: var(--color-border-default);
    color: var(--color-text-primary);
  }
  .cc-orgmodal-primary-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 1rem;
    background: var(--color-brand-primary);
    color: var(--button-primary-text, #fff);
    border: none;
    border-radius: var(--radius-md);
    font-size: 0.78rem;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.12s ease;
  }
  .cc-orgmodal-primary-btn:hover { opacity: 0.9; }

  /* ── Leaderboard Zone ────────────────────────────────────────── */
  .cc-leaders-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.875rem;
  }
  .cc-leader-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    padding: 1.25rem;
    box-shadow: var(--shadow-sm);
    animation: cc-card-up 0.5s cubic-bezier(0.22,1,0.36,1) 0.38s both;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .cc-leader-card:hover {
    border-color: var(--color-border-default);
    box-shadow: var(--shadow-md);
  }
  .cc-leader-card-danger {
    border-color: color-mix(in srgb, var(--color-error) 20%, var(--color-border-subtle));
  }
  .cc-leader-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  .cc-leader-title {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
  }
  .cc-leader-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  /* 3D Flip Card */
  .cc-flip-card {
    perspective: 800px;
    height: 52px;
    animation: cc-card-up 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }
  .cc-flip-card-inner {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .cc-flip-card--flipped .cc-flip-card-inner {
    transform: rotateX(180deg);
  }
  .cc-flip-card-front,
  .cc-flip-card-back {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-subtle);
    background: color-mix(in srgb, var(--color-bg-secondary) 92%, var(--color-bg-tertiary));
    overflow: hidden;
  }
  .cc-flip-card-back {
    transform: rotateX(180deg);
    background: color-mix(in srgb, var(--color-bg-tertiary) 90%, var(--color-bg-secondary));
  }

  /* Leader item (front) */
  .cc-leader-item {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0 0.875rem;
    height: 100%;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .cc-leader-item:hover {
    background: var(--color-bg-hover);
  }
  .cc-rank-medal {
    font-size: 1.2rem;
    width: 28px;
    text-align: center;
    flex-shrink: 0;
  }
  .cc-rank-number {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-text-muted);
    width: 28px;
    text-align: center;
    flex-shrink: 0;
  }
  .cc-leader-item-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .cc-leader-item-name {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--color-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cc-leader-item-plan {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .cc-leader-item-amount {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--color-text-primary);
    flex-shrink: 0;
  }

  /* Leader item (back) */
  .cc-leader-item-back {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.25rem 1rem;
    padding: 0.5rem 0.875rem;
    height: 100%;
    align-content: center;
    cursor: pointer;
  }
  .cc-leader-back-row {
    font-size: 0.7rem;
    color: var(--color-text-muted);
    display: flex;
    gap: 0.25rem;
  }
  .cc-leader-back-row strong {
    color: var(--color-text-primary);
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
  }

  /* Late Payment Items */
  .cc-late-item {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.625rem 0.875rem;
    border-radius: var(--radius-md);
    cursor: pointer;
    position: relative;
    transition: all 0.15s ease;
    animation: cc-card-up 0.4s cubic-bezier(0.22,1,0.36,1) both;
  }
  .cc-late-item:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  .cc-late-critical {
    background: color-mix(in srgb, var(--color-error) 8%, var(--color-bg-secondary));
    border: 1px solid color-mix(in srgb, var(--color-error) 25%, transparent);
  }
  .cc-late-warning {
    background: color-mix(in srgb, var(--color-warning) 6%, var(--color-bg-secondary));
    border: 1px solid color-mix(in srgb, var(--color-warning) 20%, transparent);
  }
  .cc-late-mild {
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border-subtle);
  }
  .cc-late-rank {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--color-text-muted);
    width: 24px;
    text-align: center;
    flex-shrink: 0;
  }
  .cc-late-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .cc-late-name {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--color-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cc-late-days {
    font-size: 0.68rem;
    color: var(--color-error);
    font-weight: 500;
  }
  .cc-late-amount {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--color-error);
    flex-shrink: 0;
  }
  .cc-late-pulse {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-error);
    animation: cc-pulse-dot 1.5s ease-in-out infinite;
  }

  /* ── Bottom Grid (Attention + Activity) ──────────────────────── */
  .cc-bottom-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.875rem;
  }
  .cc-bottom-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    padding: 1.25rem;
    box-shadow: var(--shadow-sm);
    animation: cc-card-up 0.5s cubic-bezier(0.22,1,0.36,1) 0.44s both;
    transition: border-color 0.2s ease;
  }
  .cc-bottom-card:hover { border-color: var(--color-border-default); }
  .cc-bottom-card-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    color: var(--color-text-primary);
  }
  .cc-bottom-card-title {
    font-size: 0.85rem;
    font-weight: 600;
    margin: 0;
  }

  /* Attention Items */
  .cc-attention-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .cc-attention-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border-radius: var(--radius-md);
    background: var(--color-bg-primary);
    border-left: 3px solid transparent;
    text-decoration: none;
    color: inherit;
    transition: all 0.15s ease;
    animation: cc-card-up 0.4s cubic-bezier(0.22,1,0.36,1) both;
  }
  .cc-attention-item:hover {
    transform: translateX(3px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  .cc-attention-pulse {
    animation: cc-card-up 0.4s cubic-bezier(0.22,1,0.36,1) both, cc-attention-glow 2.5s ease-in-out infinite 1s;
  }
  .cc-attention-icon {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--color-bg-tertiary);
  }
  .cc-attention-content {
    flex: 1;
    min-width: 0;
  }
  .cc-attention-title {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }
  .cc-attention-desc {
    font-size: 0.7rem;
    color: var(--color-text-muted);
  }
  .cc-attention-count {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--color-text-primary);
    flex-shrink: 0;
  }
  .cc-attention-arrow {
    flex-shrink: 0;
    opacity: 0.5;
    transition: transform 0.15s ease;
  }
  .cc-attention-item:hover .cc-attention-arrow {
    transform: translateX(3px);
    opacity: 1;
  }

  /* Activity Timeline */
  .cc-activity-list {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }
  .cc-activity-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0.5rem;
    border-radius: var(--radius-md);
    transition: background 0.12s ease;
    animation: cc-card-up 0.35s cubic-bezier(0.22,1,0.36,1) both;
  }
  .cc-activity-item:hover { background: var(--color-bg-hover); }
  .cc-activity-orb {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .cc-activity-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .cc-activity-name {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--color-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cc-activity-desc {
    font-size: 0.7rem;
    color: var(--color-text-muted);
  }
  .cc-activity-time {
    font-size: 0.68rem;
    color: var(--color-text-muted);
    flex-shrink: 0;
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
  }

  /* ── Detail Flyout ───────────────────────────────────────────── */
  .cc-flyout-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    z-index: 200;
    display: flex;
    justify-content: flex-end;
    animation: cc-overlay-in 0.2s ease both;
  }
  .cc-flyout-panel {
    width: 420px;
    max-width: 92vw;
    height: 100%;
    background: var(--color-bg-secondary);
    border-left: 1px solid var(--color-border-subtle);
    box-shadow: -8px 0 32px rgba(0,0,0,0.2);
    display: flex;
    flex-direction: column;
    animation: cc-flyout-in 0.35s cubic-bezier(0.22,1,0.36,1) both;
    overflow-y: auto;
  }
  .cc-flyout-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1.5rem 1.5rem 1rem;
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .cc-flyout-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--color-text-primary);
    margin: 0 0 0.375rem;
  }
  .cc-flyout-plan-badge {
    display: inline-block;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 0.15rem 0.5rem;
    border-radius: 9999px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .cc-flyout-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    color: var(--color-text-muted);
    cursor: pointer;
    transition: background 0.12s;
  }
  .cc-flyout-close:hover {
    background: var(--color-bg-tertiary);
  }
  .cc-flyout-body {
    flex: 1;
    padding: 1.25rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }
  .cc-flyout-metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }
  .cc-flyout-metric {
    background: var(--color-bg-tertiary);
    border-radius: var(--radius-md);
    padding: 0.75rem;
  }
  .cc-flyout-metric-label {
    font-size: 0.68rem;
    color: var(--color-text-muted);
    display: block;
    margin-bottom: 0.25rem;
  }
  .cc-flyout-metric-value {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--color-text-primary);
  }
  .cc-flyout-section { }
  .cc-flyout-section-title {
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 0.5rem;
  }
  .cc-flyout-status-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }
  .cc-flyout-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .cc-status-active { background: #10b981; }
  .cc-status-trial { background: var(--color-warning); }
  .cc-status-past_due { background: var(--color-error); }
  .cc-status-suspended { background: var(--color-error); }
  .cc-flyout-overdue-alert {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: var(--radius-md);
    background: color-mix(in srgb, var(--color-error) 8%, var(--color-bg-secondary));
    color: var(--color-error);
    font-size: 0.75rem;
    font-weight: 500;
  }
  .cc-flyout-modules {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
  }
  .cc-flyout-module-tag {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: var(--radius-sm);
    background: color-mix(in srgb, var(--color-brand-primary) 10%, var(--color-bg-secondary));
    color: var(--color-brand-primary);
  }
  .cc-flyout-joined-date {
    font-size: 0.82rem;
    color: var(--color-text-secondary);
  }
  .cc-flyout-footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--color-border-subtle);
  }
  .cc-flyout-view-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.6rem 1.25rem;
    background: var(--color-brand-primary);
    color: var(--button-primary-text, #fff);
    border: none;
    border-radius: var(--radius-md);
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s ease;
    width: 100%;
    justify-content: center;
  }
  .cc-flyout-view-btn:hover { opacity: 0.9; }

  /* ── Animations ──────────────────────────────────────────────── */
  @keyframes cc-fade-down {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes cc-card-up {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes cc-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes cc-draw-line {
    to { stroke-dashoffset: 0; }
  }
  @keyframes cc-pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%      { opacity: 0.5; transform: scale(1.5); }
  }
  @keyframes cc-attention-glow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
    50%      { box-shadow: 0 0 12px 2px rgba(239, 68, 68, 0.12); }
  }
  @keyframes cc-overlay-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes cc-flyout-in {
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
  }

  /* ── Responsive ──────────────────────────────────────────────── */
  @media (max-width: 1280px) {
    .cc-leaders-grid {
      grid-template-columns: 1fr 1fr;
    }
    .cc-leaders-grid > :last-child {
      grid-column: 1 / -1;
    }
  }
  @media (max-width: 1024px) {
    .cc-hero-grid {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto auto;
    }
    .cc-hero-card {
      grid-column: 1 / -1;
      grid-row: 1;
    }
    .cc-intel-grid {
      grid-template-columns: 1fr;
    }
    .cc-bottom-grid {
      grid-template-columns: 1fr;
    }
  }
  @media (max-width: 768px) {
    .cc-page { padding: 1rem; gap: 1rem; }
    .cc-hero-grid {
      grid-template-columns: 1fr;
    }
    .cc-hero-value { font-size: 2rem; }
    .cc-hero-body { flex-direction: column; align-items: stretch; gap: 0.75rem; }
    .cc-leaders-grid {
      grid-template-columns: 1fr;
    }
    .cc-leaders-grid > :last-child {
      grid-column: auto;
    }
    .cc-header {
      flex-direction: column;
      align-items: flex-start;
    }
    .cc-header-right {
      width: 100%;
      flex-wrap: wrap;
    }
    .cc-search-box { width: 100%; order: 3; }
    .cc-new-org-text { display: none; }
  }
  @media (max-width: 480px) {
    .cc-flyout-panel { width: 100%; }
  }

  /* ── Reduced Motion ──────────────────────────────────────────── */
  @media (prefers-reduced-motion: reduce) {
    .cc-header,
    .cc-hero-card,
    .cc-sat-card,
    .cc-intel-card,
    .cc-leader-card,
    .cc-bottom-card,
    .cc-flip-card,
    .cc-late-item,
    .cc-attention-item,
    .cc-activity-item,
    .cc-area-line,
    .cc-area-fill,
    .cc-flyout-panel,
    .cc-flyout-overlay,
    .cc-donut-animated,
    .cc-donut-fog,
    .cc-drill-org-row,
    .cc-orgmodal,
    .cc-orgmodal-overlay {
      animation: none !important;
    }
    .cc-area-line { stroke-dashoffset: 0 !important; stroke-dasharray: none !important; }
    .cc-area-fill { opacity: 1 !important; }
    .cc-flip-card-inner { transition: none !important; }
    .cc-sat-live-dot { animation: none !important; }
    .cc-late-pulse { animation: none !important; }
    .cc-donut-segment { transition: none !important; }
    .cc-drill-panel { transition: none !important; }
    .cc-drill-panel.cc-drill-in { opacity: 1; transform: none; }
    .cc-growth-pulse { animation: none !important; }
    .cc-growth-net-bar-fill { animation: none !important; }
    .cc-search-dropdown, .cc-search-result { animation: none !important; }
  }
`;
